/**
 * UNIFIED LANGUAGE RESOLVER (v4.8.457)
 *
 * Single source of truth for language preference state on the client.
 * Replaces the legacy fragmented keys `preferredLanguage` and `aureus_public_language`.
 *
 * Resolution order (highest priority first):
 *   1. signed-in account `users.pref_language` when it is a dashboard language
 *   2. a language the visitor chose on this browser (`aureus_language_explicit`)
 *   3. English
 *
 * Browser language and country suggestions are not used. A stored language
 * without an explicit choice is discarded so an old automatic French (or other)
 * guess cannot reopen the site in that language.
 */

export const CANONICAL_LANG_KEY = 'aureus_language'
export const EXPLICIT_FLAG_KEY = 'aureus_language_explicit'
export const ACCOUNT_LANG_KEY = 'aureus_account_language'
const SESSION_ID_KEY = 'aureus_server_session_id'
export const LEGACY_KEYS = ['preferredLanguage', 'aureus_public_language'] as const

export interface SupportedLanguage {
  code: string
  name: string
  nativeName: string
  flag: string
  rtl?: boolean
}

export const SUPPORTED_LANGUAGES: ReadonlyArray<SupportedLanguage> = [
  { code: 'en', name: 'English',    nativeName: 'English',   flag: '🇬🇧' },
  { code: 'fr', name: 'French',     nativeName: 'Français',  flag: '🇫🇷' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'es', name: 'Spanish',    nativeName: 'Español',   flag: '🇪🇸' },
  { code: 'ar', name: 'Arabic',     nativeName: 'العربية',    flag: '🇸🇦', rtl: true },
  { code: 'sw', name: 'Swahili',    nativeName: 'Kiswahili', flag: '🇰🇪' },
  { code: 'zu', name: 'Zulu',       nativeName: 'isiZulu',   flag: '🇿🇦' },
  { code: 'hi', name: 'Hindi',      nativeName: 'हिन्दी',      flag: '🇮🇳' },
  { code: 'zh', name: 'Chinese',    nativeName: '中文',       flag: '🇨🇳' },
]

export const SUPPORTED_CODES: ReadonlySet<string> = new Set(SUPPORTED_LANGUAGES.map(l => l.code))

export function isSupported(code: string | null | undefined): code is string {
  return !!code && SUPPORTED_CODES.has(code.toLowerCase())
}

function safeGet(key: string): string | null {
  try { return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null }
  catch { return null }
}

function safeSet(key: string, value: string): void {
  try { if (typeof window !== 'undefined') window.localStorage.setItem(key, value) } catch {}
}

function safeRemove(key: string): void {
  try { if (typeof window !== 'undefined') window.localStorage.removeItem(key) } catch {}
}

/**
 * One-time migration: read any legacy key, copy into canonical key, then clear legacy.
 * Does NOT set the explicit flag (legacy data is treated as soft preference, not explicit).
 */
export function migrateLegacyKeys(): void {
  const existing = safeGet(CANONICAL_LANG_KEY)
  if (existing && isSupported(existing)) return
  for (const legacy of LEGACY_KEYS) {
    const v = safeGet(legacy)
    if (v && isSupported(v)) {
      safeSet(CANONICAL_LANG_KEY, v.toLowerCase())
      break
    }
  }
  for (const legacy of LEGACY_KEYS) safeRemove(legacy)
}

export function getStoredLanguage(): string | null {
  const v = safeGet(CANONICAL_LANG_KEY)
  return isSupported(v) ? (v as string).toLowerCase() : null
}

export function hasUserExplicitlyChosenLanguage(): boolean {
  return safeGet(EXPLICIT_FLAG_KEY) === '1'
}

function hasSession(): boolean {
  return !!safeGet(SESSION_ID_KEY)
}

/** Drop a language that was saved automatically. An explicit visitor choice stays. */
export function discardAutomaticLanguage(): void {
  if (!hasUserExplicitlyChosenLanguage()) safeRemove(CANONICAL_LANG_KEY)
}

/** Remember the signed-in account language for the next open. Pass null to forget it. */
export function rememberAccountLanguage(code: string | null | undefined): void {
  if (!isSupported(code)) {
    safeRemove(ACCOUNT_LANG_KEY)
    return
  }
  safeSet(ACCOUNT_LANG_KEY, code.toLowerCase())
}

/**
 * First paint language. English, unless this browser has an explicit choice,
 * or a signed-in session already has a saved account language.
 */
export function resolveBootLanguage(): string {
  try { migrateLegacyKeys() } catch { /* storage unavailable */ }
  discardAutomaticLanguage()
  if (hasSession()) {
    const account = safeGet(ACCOUNT_LANG_KEY)
    if (isSupported(account)) return (account as string).toLowerCase()
  }
  if (hasUserExplicitlyChosenLanguage()) {
    const stored = getStoredLanguage()
    if (stored) return stored
  }
  return 'en'
}

export function setStoredLanguage(code: string, opts?: { explicit?: boolean }): void {
  if (!isSupported(code)) return
  const normalized = code.toLowerCase()
  safeSet(CANONICAL_LANG_KEY, normalized)
  if (opts?.explicit) {
    safeSet(EXPLICIT_FLAG_KEY, '1')
    if (hasSession()) rememberAccountLanguage(normalized)
  }
}

export function getBrowserLanguage(): string {
  const raw = typeof navigator !== 'undefined' ? navigator.language : 'en'
  const short = (raw || 'en').split('-')[0]?.toLowerCase() || 'en'
  return isSupported(short) ? short : 'en'
}

/** Country suggestion. Not used when opening the site. English is the default. */
export async function fetchGeoSuggestedLanguage(timeoutMs = 1500): Promise<string | null> {
  if (typeof window === 'undefined') return null
  try {
    const ctrl = new AbortController()
    const t = setTimeout(() => ctrl.abort(), timeoutMs)
    const res = await fetch('/api/public/geo-language', { signal: ctrl.signal, credentials: 'omit' })
    clearTimeout(t)
    if (!res.ok) return null
    const data = await res.json().catch(() => null) as { suggestedLanguage?: string } | null
    const code = data?.suggestedLanguage?.toLowerCase()
    return isSupported(code) ? (code as string) : null
  } catch { return null }
}

/**
 * Async hydration. Caller passes optional `users.pref_language`.
 * A saved account language wins over a browser choice. Otherwise English,
 * unless the visitor already chose a language on this browser.
 */
export async function resolveInitialLanguage(dbValue?: string | null): Promise<{ code: string; source: 'localStorage'|'db'|'fallback' }> {
  if (isSupported(dbValue)) {
    const code = (dbValue as string).toLowerCase()
    rememberAccountLanguage(code)
    return { code, source: 'db' }
  }
  migrateLegacyKeys()
  discardAutomaticLanguage()
  if (hasSession()) {
    const account = safeGet(ACCOUNT_LANG_KEY)
    if (isSupported(account)) return { code: (account as string).toLowerCase(), source: 'db' }
  }
  if (hasUserExplicitlyChosenLanguage()) {
    const stored = getStoredLanguage()
    if (stored) return { code: stored, source: 'localStorage' }
  }
  return { code: 'en', source: 'fallback' }
}

/** Persist explicit selection to the authenticated user's profile (best-effort). */
export async function persistLanguageToDb(language: string, ensureSessionHeaders: () => Promise<Record<string,string>>): Promise<boolean> {
  if (!isSupported(language)) return false
  try {
    const res = await fetch('/api/user/update-profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
      body: JSON.stringify({ operation: 'update_language', data: { language } }),
    })
    return res.ok
  } catch { return false }
}
